<template>
  <div
    v-if="isOpen"
    ref="modalRef"
    class="recruitment-modal"
    :style="modalStyle"
    @mousedown="bringToFront"
  >
    <!-- Header (draggable) -->
    <div
      ref="headerRef"
      class="modal-header"
      :class="{ flashing: isFlashing }"
      @mousedown="startDrag"
    >
      <span class="modal-title">ENLIST</span>
      <button class="close-btn" @click="close" title="Close"><XMarkIcon class="close-icon" /></button>
    </div>

    <!-- Content -->
    <div class="modal-content">
      <!-- Animated Background -->
      <div class="animated-bg">
        <div class="grid-lines"></div>
        <div class="glow-orb orb-1"></div>
        <div class="glow-orb orb-2"></div>
        <div class="scanline"></div>
      </div>

      <!-- Main Content -->
      <div class="content-inner">
        <!-- Main Title -->
        <h2 class="main-title">ENLISTMENT</h2>
        <p class="coming-soon-label">COMING SOON</p>

        <!-- Divider -->
        <div class="divider"></div>

        <!-- Subtitle -->
        <p class="subtitle">Follow us on X to prepare</p>

        <!-- Follow Button -->
        <a
          href="https://x.com/megadotarmy"
          target="_blank"
          rel="noopener noreferrer"
          class="follow-btn"
        >
          <svg class="x-logo" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" fill="currentColor"/>
          </svg>
          <span>FOLLOW @MEGADOTARMY</span>
        </a>
      </div>

      <!-- Mascot Image (Background) -->
      <div class="mascot-bg">
        <img src="/rabbit.webp" alt="MEGA Army Mascot" class="mascot-image" @error="($event.target as HTMLImageElement).style.display = 'none'" />
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import { XMarkIconSolid as XMarkIcon } from '@/components/icons';
import { useWallet } from '@/composables/useWallet';

// Props
export interface RecruitmentModalProps {
  zIndexBase?: number;
}

const props = withDefaults(defineProps<RecruitmentModalProps>(), {
  zIndexBase: 1000
});

// Emits
const emit = defineEmits<{
  close: [];
  focus: [];
}>();

// Wallet (kept for future use)
const { isConnected, isConnecting, truncatedAddress, connect, disconnect } = useWallet();

// State
const isOpen = ref(false);

// Position and size
const x = ref(100);
const y = ref(100);
const width = ref(400);
const height = ref(580);
const zIndex = ref(props.zIndexBase);

// Flash animation
const isFlashing = ref(false);

// Dragging state
const isDragging = ref(false);
const dragStartX = ref(0);
const dragStartY = ref(0);
const dragOffsetX = ref(0);
const dragOffsetY = ref(0);

// Refs
const modalRef = ref<HTMLElement>();
const headerRef = ref<HTMLElement>();

// Computed style
const modalStyle = computed(() => ({
  left: `${x.value}px`,
  top: `${y.value}px`,
  width: `${width.value}px`,
  height: `${height.value}px`,
  zIndex: zIndex.value
}));

/**
 * Handle connect button click
 */
const handleConnect = async () => {
  await connect();
};

/**
 * Handle disconnect button click
 */
const handleDisconnect = async () => {
  await disconnect();
};

/**
 * Trigger flash animation on header
 */
const flashHeader = () => {
  isFlashing.value = true;
  setTimeout(() => {
    isFlashing.value = false;
  }, 500);
};

/**
 * Open modal centered on screen
 */
const open = () => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  x.value = Math.max(20, (viewportWidth - width.value) / 2);
  y.value = Math.max(70, (viewportHeight - height.value) / 2);

  isOpen.value = true;
  flashHeader();
};

/**
 * Close modal
 */
const close = () => {
  isOpen.value = false;
  emit('close');
};

/**
 * Bring modal to front (no flash - flash only on external focus)
 */
const bringToFront = () => {
  emit('focus');
};

/**
 * Set z-index externally
 */
const setZIndex = (newZIndex: number) => {
  zIndex.value = newZIndex;
};

/**
 * Start dragging
 */
const startDrag = (e: MouseEvent) => {
  if (e.target !== headerRef.value && !(headerRef.value?.contains(e.target as Node))) {
    return;
  }

  if ((e.target as HTMLElement).classList.contains('close-btn')) {
    return;
  }

  isDragging.value = true;
  dragStartX.value = e.clientX;
  dragStartY.value = e.clientY;
  dragOffsetX.value = x.value;
  dragOffsetY.value = y.value;

  document.addEventListener('mousemove', handleDrag);
  document.addEventListener('mouseup', stopDrag);

  e.preventDefault();
};

/**
 * Handle drag movement
 */
const handleDrag = (e: MouseEvent) => {
  if (!isDragging.value) return;

  const deltaX = e.clientX - dragStartX.value;
  const deltaY = e.clientY - dragStartY.value;

  let newX = dragOffsetX.value + deltaX;
  let newY = dragOffsetY.value + deltaY;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  newX = Math.max(0, Math.min(newX, viewportWidth - width.value));
  newY = Math.max(0, Math.min(newY, viewportHeight - 50));

  x.value = newX;
  y.value = newY;
};

/**
 * Stop dragging
 */
const stopDrag = () => {
  isDragging.value = false;
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
};

// Cleanup on unmount
onUnmounted(() => {
  document.removeEventListener('mousemove', handleDrag);
  document.removeEventListener('mouseup', stopDrag);
});

// Expose methods for parent component
defineExpose({
  open,
  close,
  setZIndex,
  flashHeader,
  isOpen: () => isOpen.value
});
</script>

<style scoped>
.recruitment-modal {
  position: fixed;
  background: var(--bg-surface);
  border: 2px solid var(--accent-secondary);
  display: flex;
  flex-direction: column;
  font-family: Inter, Arial, sans-serif;
  user-select: none;
  box-shadow:
    0 0 40px rgba(249, 115, 22, 0.15),
    0 0 80px rgba(249, 115, 22, 0.05),
    var(--shadow-lg);
  border-radius: 8px;
  overflow: hidden;
}

/* Header */
.modal-header {
  height: 48px;
  background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%);
  border-bottom: 1px solid var(--accent-secondary);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 16px;
  cursor: move;
  flex-shrink: 0;
  transition: background 0.3s;
  position: relative;
}

.modal-header.flashing {
  animation: headerFlash 500ms ease-out;
}

@keyframes headerFlash {
  0% { background: var(--accent-secondary); }
  100% { background: linear-gradient(180deg, var(--bg-elevated) 0%, var(--bg-surface) 100%); }
}

.modal-title {
  color: var(--accent-secondary);
  font-size: 15px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 3px;
  text-shadow: 0 0 20px rgba(249, 115, 22, 0.5);
}

.close-btn {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 1px solid var(--error);
  color: var(--error);
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
}

.close-btn:hover {
  background: var(--error);
  color: var(--text-inverse);
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.5);
}

.close-icon {
  width: 16px;
  height: 16px;
}

/* Content */
.modal-content {
  flex: 1;
  position: relative;
  overflow: hidden;
}

/* Animated Background */
.animated-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.grid-lines {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(249, 115, 22, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(249, 115, 22, 0.03) 1px, transparent 1px);
  background-size: 30px 30px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(30px, 30px); }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.4;
}

.orb-1 {
  width: 200px;
  height: 200px;
  background: var(--accent-secondary);
  top: -50px;
  right: -50px;
  animation: orbFloat1 8s ease-in-out infinite;
}

.orb-2 {
  width: 150px;
  height: 150px;
  background: var(--accent-primary);
  bottom: -30px;
  left: -30px;
  animation: orbFloat2 10s ease-in-out infinite;
}

@keyframes orbFloat1 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.4; }
  50% { transform: translate(-20px, 20px) scale(1.1); opacity: 0.6; }
}

@keyframes orbFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.3; }
  50% { transform: translate(20px, -20px) scale(1.2); opacity: 0.5; }
}

.scanline {
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-secondary), transparent);
  opacity: 0.3;
  animation: scanMove 4s linear infinite;
}

@keyframes scanMove {
  0% { top: -2px; }
  100% { top: 100%; }
}

/* Content Inner */
.content-inner {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 40px 24px 24px;
  height: 100%;
  gap: 8px;
}

/* Typography */
.main-title {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: 4px;
  color: var(--accent-secondary);
  margin: 0;
  text-shadow: 0 0 30px rgba(249, 115, 22, 0.5);
}

.coming-soon-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 3px;
  color: var(--text-primary);
  margin: 0;
}

.divider {
  width: 60px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--accent-secondary), transparent);
  margin: 12px 0;
}

.subtitle {
  font-size: 13px;
  color: var(--text-primary);
  text-align: center;
  margin: 0 0 8px 0;
}

/* Follow Button */
.follow-btn {
  padding: 16px 32px;
  font-size: 13px;
  font-weight: 800;
  font-family: Inter, Arial, sans-serif;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 8px;
  background: var(--accent-secondary);
  border: none;
  color: #fff;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  box-shadow: 0 4px 20px rgba(249, 115, 22, 0.4);
}

.follow-btn:hover {
  background: #ea7c2b;
  box-shadow: 0 6px 30px rgba(249, 115, 22, 0.5);
  transform: translateY(-2px);
}

.follow-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 10px rgba(249, 115, 22, 0.4);
}

.x-logo {
  width: 18px;
  height: 18px;
}

/* Mascot Background
   Edit these values to adjust the rabbit position and opacity:
   - bottom: move up (positive %) or down (negative %)
   - opacity: 0 to 1 (0.25 = 25% visible)
*/
.mascot-bg {
  position: absolute;
  bottom: -5%;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  height: 70%;
  pointer-events: none;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: hidden;
}

.mascot-image {
  width: 100%;
  max-width: 380px;
  height: auto;
  object-fit: contain;
  opacity: 0.85;
  filter: drop-shadow(0 0 30px rgba(249, 115, 22, 0.4));
  transform: scale(0.80);
  
}
</style>
